/**
 * Load predefined CSS and JavaScript modules dynamically
 *
 * @package stylr
 */
import devlog from './utility/devlog';
import includeCSS from './utility/include-css';
import includeJS from './utility/include-js';

export default class ModuleLoader {

    /**
     * Setup class properties
     *
     * @param {null|object} map
     * @param {int} timeout
     */
    constructor( map = null, path = '', timeout = 300 ) {
		this.map = map;
		this.path = path;
        this.timeout = timeout;
    }

    /**
     * Load a registered module
     * @param {string} name
     */
    include( name ) {
        let mods = this.map;
        let mod = mods[name] || null;
        let timeout = this.timeout;

        /**
         * Check if module is registered
         */
        if ( ! mod ) {
            devlog(
                `%cModule - "${name}" not found in registry`,
                'color: red'
            );
            return Promise.reject(
                new Error( `${name} module is not registered` )
            );
        }

        /**
         * Check if module failed to load
         */
        if ( mod.failed ) {
            devlog(
                `%cModule - "${name}" tried and failed to load before`,
                'color: grey'
            );
            return Promise.resolve( `${name} tried to load before and failed` );
        }

        /**
         * Check if module is already loaded
         */
        if ( mod.loaded ) {
            devlog( `%cModule - "${name}" is already loaded`, 'color: grey' );
            return Promise.resolve( `${name} is already loaded` );
        }

        /**
         * Check if module is being included at the moment
         * wait a bit and try again if it is being included
         */
        if ( mod.including ) {
            return new Promise( ( resolve, reject ) => {
                setTimeout( () => {
                    this.include( name )
                        .catch( e => reject( e ) )
                        .then( m => resolve( m ) );
                }, timeout );
            });
        }

        /**
         * Load dependencies
         */
        if ( mod.dependencies && 0 < mod.dependencies.length ) {
            let dependencies = [];
            mod.dependencies.forEach( dep => {
                if ( ! mods[dep]) {
                    return Promise.reject(
                        new Error(
                            'Module "' + name +
                            '" has an undefined dependency "' + dep + '"'
                        )
                    );
                }
                if ( ! mods[dep].loaded ) {
                    dependencies.push( this.include( dep ) );
                }
            });
            if ( 0 < dependencies.length ) {
                return Promise.all( dependencies )
                    .catch( e => Promise.reject( new Error( e ) ) )
                    .then( m => this.include( name ) );
            }
        }

        /**
         * Set including flag
         */
        mod.including = true;

        /**
         * Load module files
         */
        let files = [];
        mod.files.forEach( file => {
            files.push( this.includeFile( file, name ) );
        });
        return Promise.all( files )
			.catch( e => {
				mod.including = false;
				mod.failed = true;
				devlog( `%cModule load failed "${name}"`, 'color: red' );
				Promise.reject( new Error( e ) );
			})
			.then( () => {
				mod.including = false;
				mod.loaded = true;
				devlog( `%cModule loaded "${name}"`, 'color: green' );
				Promise.resolve( `${name} module loaded` );
			});
    }

    /**
     * Load a CSS or JavaScript file
     * @param {string|object} file
     * @param {string|null} name
     * @return void
     */
    includeFile( file, name = null ) {
        return new Promise( ( resolve, reject ) => {

            /**
             * Sanitize passed-in file
             */
            if ( 'string' === typeof file ) {
                file = { url: this.path + file };
            }
            if ( file.local ) {
                file.local = this.path + file.local;
            }
            if ( ! name ) {
                name = file.local;
            }

            /**
             * Get external URL or local fallback path
             */
            let url = file.url && ! file.fallback ? file.url : null;
            if ( ! url && file.local ) {
                url = file.local;
            }
            if ( ! url ) {
                devlog( `%cModule - "${name}" file not found`, 'color: red' );
                reject( new Error( `${name} failed to load` ) );
            }
            file.loaded = false;

            /**
             * Load module file
             */
            let loader = 'css' === file.type ? includeCSS : includeJS;
            loader( url ).then( () => {
                file.loaded = true;
                resolve( `${name} loaded` );
            }).catch( () => {

                /**
                 * Fallback to local file
                 */
                devlog(
                    `%cModule - "${name}" falling back to local version`,
                    'color: orange'
                );
                if ( file.local && file.local != url ) {
                    file.fallback = true;
                    this.includeFile( file, name )
                        .catch( e => reject( new Error( e ) ) )
                        .then( m => resolve( m ) );
                } else {
                    reject( new Error( `${name} failed to load` ) );
                }
            });
        });
    }
}
